import React, { useMemo, memo } from 'react';
import {
  Paper,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Stack,
  Tooltip,
  Fade,
  Select,
  MenuItem,
  FormControl,
  Chip,
  LinearProgress,
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import DownloadIcon from '@mui/icons-material/Download';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import useActiveUser from '../collaboration/useActiveUser';
import { parseHighlightedText } from './SearchBar';
import { DiaryItem } from '../hooks/useDiaryItems';
import { DocumentItem } from '../hooks/useDocuments';

const CATEGORIES = {
  expense: 'Expenses',
  income: 'Sales',
  'bank statement': 'Bank Statements',
  other: 'Documents',
};

interface DocumentGridItemProps {
  document: DiaryItem | DocumentItem;
  onRowClick: (document: DiaryItem | DocumentItem) => void;
  onImageOpen: (document: DiaryItem | DocumentItem) => void;
  onDownload?: (document: DiaryItem | DocumentItem) => void;
  imageUrls: {
    [id: string]: {
      thumbnail: string | null;
      fullSize: string | null;
      failed?: boolean;
    };
  };
  getValidDate: (doc: DiaryItem | DocumentItem) => Date;
  showPendingBar?: boolean;
  smartTruncate?: (message: any) => string;
  transparentBackground?: boolean;
  thumbnailSize?: { xs: number; sm: number; md: number };
}

const DocumentGridItem: React.FC<DocumentGridItemProps> = memo(
  ({
    document,
    onRowClick,
    onImageOpen,
    onDownload,
    imageUrls,
    getValidDate,
    showPendingBar = true,
    smartTruncate,
    transparentBackground = false,
    thumbnailSize = { xs: 72, sm: 84, md: 96 },
  }) => {
    const { t, i18n } = useTranslation();
    const { activeUser } = useActiveUser();

    // Memoize expensive calculations with stable dependencies
    const isUnread = useMemo(() => {
      // Check if item has unread messages
      // No need to check unread status if there's no user or no message
      if (!activeUser?.id || !document.lastAssistantMessage) {
        return false;
      }

      // Add a buffer time (3 seconds) to account for timestamp synchronization issues
      const BUFFER_TIME = 3000; // 3 seconds in milliseconds

      // Parse last_read_data if it's a string from database
      let lastReadData = null;
      try {
        if (document.last_read_data) {
          // Handle both string and object formats
          if (typeof document.last_read_data === 'string') {
            lastReadData = JSON.parse(document.last_read_data);
          } else {
            lastReadData = document.last_read_data;
          }
        }
      } catch (error) {
        console.error('Error parsing last_read_data:', error);
        return true; // If we can't parse it, consider it unread
      }

      // Check if this user has read this item using the structured data
      const userHasReadItem =
        lastReadData && lastReadData.user_id === activeUser.id;
      const userLastRead = userHasReadItem ? lastReadData.timestamp : undefined;

      // If never read before by this user, it's unread
      if (!userHasReadItem || userLastRead === undefined) {
        return true;
      }

      // Only check message timestamps, not modified_at
      const msgTime: number | undefined =
        document?.lastAssistantMessageTimestamp;

      // Only consider it unread if there are new messages
      if (msgTime && userLastRead && msgTime > userLastRead + BUFFER_TIME) {
        return true;
      }

      return false;
    }, [
      activeUser?.id,
      document.lastAssistantMessage,
      document.last_read_data,
      document.lastAssistantMessageTimestamp,
    ]);

    // Determine display text with special handling for chat type items
    const displayText = useMemo(() => {
      if (document.highlightedContent) {
        return smartTruncate
          ? smartTruncate(document.highlightedContent)
          : document.highlightedContent;
      }
      if (document.type === 'NewChat') {
        return document.lastAssistantMessage
          ? document.lastAssistantMessage
          : document.description || t('No description');
      } else {
        return (
          (document.lastAssistantMessage
            ? document.lastAssistantMessage
            : null) ||
          document.description ||
          t('No description')
        );
      }
    }, [
      document.highlightedContent,
      document.type,
      document.lastAssistantMessage,
      document.description,
      t,
      smartTruncate,
    ]);

    // Get appropriate icon based on item type or title - memoized with stable dependencies
    const getPlaceholderIcon = useMemo(() => {
      const itemType = document.type?.toLowerCase() || '';
      const itemTitle = document.title?.toLowerCase() || '';
      const description = document.description?.toLowerCase() || '';

      // Chat type items
      if (itemType === 'newchat') {
        return <ChatOutlinedIcon sx={{ fontSize: 26, color: '#ffffff' }} />;
      }
      // Receipt related
      else if (
        itemType.includes('receipt') ||
        itemTitle.includes('tosite') ||
        itemTitle.includes('receipt') ||
        itemTitle.includes('kuitti')
      ) {
        return <ReceiptOutlinedIcon sx={{ fontSize: 26, color: '#ffffff' }} />;
      }
      // Bank related
      else if (
        itemType.includes('bank') ||
        itemTitle.includes('pankkitili') ||
        itemType.includes('bank') ||
        itemTitle.includes('pankki')
      ) {
        return (
          <CreditCardOutlinedIcon sx={{ fontSize: 26, color: '#ffffff' }} />
        );
      }
      // Accountant related
      else if (
        itemType.includes('accountant') ||
        itemTitle.includes('kirjanpitäjä') ||
        itemTitle.includes('kirjanpitäjäsi')
      ) {
        return (
          <PersonOutlineOutlinedIcon sx={{ fontSize: 26, color: '#ffffff' }} />
        );
      }
      // Business related
      else if (
        itemType.includes('business') ||
        itemTitle.includes('liiketoiminnastasi') ||
        itemTitle.includes('liiketoiminta') ||
        itemTitle.includes('yritykse')
      ) {
        return <BusinessOutlinedIcon sx={{ fontSize: 26, color: '#ffffff' }} />;
      }
      // File sharing related
      else if (
        itemTitle.includes('jaa') ||
        itemTitle.includes('share') ||
        description.includes('kuva') ||
        description.includes('tiedosto')
      ) {
        return <ShareOutlinedIcon sx={{ fontSize: 26, color: '#ffffff' }} />;
      }
      // Calendar/Events related
      else if (itemType.includes('event') || itemTitle.includes('tapahtum')) {
        return (
          <CalendarTodayOutlinedIcon sx={{ fontSize: 26, color: '#ffffff' }} />
        );
      }
      // Email related
      else if (
        itemTitle.includes('email') ||
        itemTitle.includes('sähköposti') ||
        description.includes('email') ||
        description.includes('sähköposti')
      ) {
        return (
          <MailOutlineOutlinedIcon sx={{ fontSize: 26, color: '#ffffff' }} />
        );
      }
      // Default fallback
      else {
        // Log unknown types for future improvements
        console.debug('Unknown item type for icon:', {
          type: itemType,
          title: itemTitle,
        });
        return (
          <DescriptionOutlinedIcon sx={{ fontSize: 26, color: '#ffffff' }} />
        );
      }
    }, [document.type, document.title, document.description]);

    // Memoize the formatted date to avoid recalculation
    const formattedDate = useMemo(() => {
      return format(getValidDate(document), 'dd.MM.yyyy');
    }, [document, getValidDate]);

    // Memoize the formatted amount to avoid recalculation
    const formattedAmount = useMemo(() => {
      if (!document.amount) return null;
      return new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency: 'EUR',
      }).format(document.amount);
    }, [document.amount, i18n.language]);

    // Memoize the highlighted title parsing
    const parsedTitle = useMemo(() => {
      return document.highlightedTitle
        ? parseHighlightedText(document.highlightedTitle)
        : document.title || t('New chat');
    }, [document.highlightedTitle, document.title, t]);

    // Memoize the highlighted content parsing
    const parsedContent = useMemo(() => {
      if (!document.highlightedContent) return null;
      const content = smartTruncate
        ? smartTruncate(document.highlightedContent)
        : document.highlightedContent;
      return parseHighlightedText(content);
    }, [document.highlightedContent, smartTruncate]);

    // Memoize the image URL to avoid unnecessary re-renders
    const imageUrl = useMemo(() => {
      return imageUrls[document.id]?.thumbnail;
    }, [imageUrls, document.id]);

    // Memoize the image loading state
    const isImageLoading = useMemo(() => {
      return !imageUrls[document.id];
    }, [imageUrls, document.id]);

    // Memoize the has content check
    const hasContent = useMemo(() => {
      return !!document.content?.[0]?.path;
    }, [document.content]);

    // Memoize the click handlers to prevent unnecessary re-renders
    const handleRowClick = useMemo(
      () => () => onRowClick(document),
      [onRowClick, document]
    );
    const handleImageOpen = useMemo(
      () => (e: React.MouseEvent) => {
        e.stopPropagation();
        const photoContent = document.content?.[0];
        console.log('[DocumentGridItem] onImageOpen called for document:', {
          id: document.id,
          hash: photoContent?.hash,
          path: photoContent?.path,
          thumbnail: photoContent?.thumbnail,
        });
        onImageOpen(document);
      },
      [onImageOpen, document]
    );

    const handleDownload = useMemo(
      () =>
        onDownload
          ? (e: React.MouseEvent) => {
              e.stopPropagation();
              onDownload(document);
            }
          : undefined,
      [onDownload, document]
    );

    return (
      <Paper
        onClick={handleRowClick}
        sx={{
          cursor: 'pointer',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          msUserSelect: 'none',
          ...(transparentBackground
            ? {}
            : {
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                  backgroundColor: '#F8F8FB7F',
                  boxShadow: 0,
                },
              }),
          display: 'flex',
          flexDirection: 'row',
          backgroundColor: transparentBackground
            ? 'transparent !important'
            : '#F8F8FB',
          borderRadius: transparentBackground ? 0 : 2,
          border: 'none',
          boxShadow: transparentBackground ? 'none' : undefined,
          alignItems: 'center',
          p: transparentBackground ? 0 : 1.5,
          minWidth: 0,
          flex: 1,
          position: 'relative',
          ...(showPendingBar && document.status === 'PENDING'
            ? {
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '6px',
                  backgroundColor: '#ff3b30b3',
                },
              }
            : {
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '6px',
                  backgroundColor: '#4CAF50b3',
                },
              }),
          '& > *': {
            minWidth: 0,
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: {
              xs: thumbnailSize.xs,
              sm: thumbnailSize.sm,
              md: thumbnailSize.md,
            },
            minWidth: {
              xs: thumbnailSize.xs,
              sm: thumbnailSize.sm,
              md: thumbnailSize.md,
            },
            height: {
              xs: thumbnailSize.xs,
              sm: thumbnailSize.sm,
              md: thumbnailSize.md,
            },
            backgroundColor: hasContent ? 'rgba(0, 0, 0, 0.04)' : '#8B5CF6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
            mr: 1.5,
            '&:hover .imageActions': {
              opacity: 1,
            },
          }}
        >
          {hasContent ? (
            isImageLoading ? (
              <CircularProgress
                size={20}
                sx={{ color: 'rgba(0, 0, 0, 0.2)' }}
              />
            ) : (
              <>
                {imageUrl && (
                  <img
                    src={imageUrl}
                    alt="Receipt"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 8,
                    }}
                  />
                )}
                <Box
                  className="imageActions"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    p: { xs: 0.25, sm: 0.5 },
                    display: 'flex',
                    gap: { xs: 0.25, sm: 0.5 },
                    opacity: 0,
                    transition: 'opacity 0.15s ease',
                  }}
                >
                  <IconButton
                    onClick={handleImageOpen}
                    sx={{
                      width: { xs: 28, sm: 32, md: 36 },
                      height: { xs: 28, sm: 32, md: 36 },
                      backgroundColor: 'rgba(255, 255, 255, 0.92)',
                      '&:hover': {
                        backgroundColor: 'white',
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.15s ease',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                    }}
                  >
                    <ZoomInIcon sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }} />
                  </IconButton>
                  {handleDownload && (
                    <IconButton
                      onClick={handleDownload}
                      sx={{
                        width: { xs: 28, sm: 32, md: 36 },
                        height: { xs: 28, sm: 32, md: 36 },
                        backgroundColor: 'rgba(255, 255, 255, 0.92)',
                        '&:hover': {
                          backgroundColor: 'white',
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.15s ease',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                      }}
                    >
                      <DownloadIcon
                        sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}
                      />
                    </IconButton>
                  )}
                </Box>
              </>
            )
          ) : (
            <Box
              sx={{
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              {getPlaceholderIcon}
            </Box>
          )}
        </Box>
        <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: isUnread ? 700 : 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                lineHeight: 1.2,
                fontSize: '0.875rem',
                '& > span.title': {
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                },
              }}
            >
              <span
                className="title"
                dangerouslySetInnerHTML={{ __html: parsedTitle }}
              />
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flexShrink: 0, fontSize: '0.75rem' }}
            >
              {formattedDate}
            </Typography>
          </Stack>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {parsedContent ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  fontSize: '0.75rem',
                  pr: 0,
                  minWidth: 0,
                  flex: 1,
                }}
                dangerouslySetInnerHTML={{ __html: parsedContent }}
              />
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  fontSize: '0.75rem',
                  pr: 0,
                  minWidth: 0,
                  flex: 1,
                }}
              >
                {displayText}
              </Typography>
            )}
            {formattedAmount && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  color:
                    document.amount && document.amount > 0
                      ? '#2E7D32'
                      : '#D32F2F',
                }}
              >
                {formattedAmount}
              </Typography>
            )}
          </Box>
        </Stack>
      </Paper>
    );
  }
);

DocumentGridItem.displayName = 'DocumentGridItem';

export default DocumentGridItem;
